import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { Observable } from 'rxjs';
import { FormBase } from '../../../_classes/form-base.class';
import { EResources } from '../../../_resources/EResources';
import { CustomersService } from '../../../_services/customers/customers.service';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { SessionService } from '../../../_services/session/session.service';
import { UsersSettingsService } from '../../../_services/users/users-settings.service';
import { ValidatorService } from '../../../_services/validators/validator.service';
import { AdminFormActionsComponent } from '../../admin-ui/admin-form-actions/admin-form-actions.component';
import { AdminPasswordRulesComponent } from '../../admin-ui/admin-password-rules/admin-password-rules.component';

@Component({
  standalone: true,
  selector: 'app-form-update-password',
  templateUrl: './form-update-password.component.html',
  styleUrls: ['./form-update-password.component.scss'],
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    ButtonModule,
    CommonModule,
    PasswordModule,
    AdminPasswordRulesComponent,
    AdminFormActionsComponent,
  ],
})
export class FormUpdatePasswordComponent
  extends FormBase<{
    id: number;
    password: string;
    password_confirmation: string;
  }>
  implements OnInit
{
  @Input('administration') administration = false;

  public submitted = false;
  public submitting = false;

  public form: ReturnType<FormBuilder['group']>;

  constructor(
    public readonly settingService: UsersSettingsService,
    public readonly customerService: CustomersService,
    public readonly errorsService: ErrorsService,
    public readonly messageService: MessageService,
    public readonly validatorService: ValidatorService,
    public readonly translate: TranslateService,
    public readonly session: SessionService,
    private readonly fb: FormBuilder,
  ) {
    super(session, messageService, translate);
    this.mode = 'edit';
    this.form = this.fb.group({
      password: ['', [Validators.required]],
      password_confirmation: ['', [Validators.required]],
    });
  }

  async execute(): Promise<void> {
    this.submitted = true;
    this.clearErrors();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.payload = {
      id: this.id,
      password: this.form.controls['password'].value || '',
      password_confirmation: this.form.controls['password_confirmation'].value || '',
    };

    this.submitting = true;
    try {
      await super.execute();
    } finally {
      this.submitting = false;
    }
  }

  clear(): void {
    this.payload = { id: this.id, password: '', password_confirmation: '' };
    this.form.reset({
      password: '',
      password_confirmation: '',
    });
    this.submitted = false;
  }

  generatePayload(): {
    id: number;
    password: string;
    password_confirmation: string;
  } {
    return this.payload;
  }

  getResourceName(): string {
    return EResources.USERS_PASSWORD;
  }

  updateFn(payload: {
    password: string;
    password_confirmation: string;
  }): Observable<any> {
    if (this.administration) {
      return this.customerService.updateUserPassword(
        this.customers_id,
        this.id,
        payload.password,
      );
    }

    return this.settingService.updatePassword(this.id, payload.password);
  }

  createFn(payload: {
    password: string;
    password_confirmation: string;
  }): Observable<any> {
    throw new Error('Method not implemented.');
  }

  properties(): string[] {
    return ['password', 'password_confirmation'];
  }

  validate(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.validatorService.password(this.payload.password)) {
        this.errors.password = this.translate.instant('ERRORS.PASSWORD.INVALID');
        return reject(this.errors);
      }

      if (this.payload.password !== this.payload.password_confirmation) {
        this.errors.password_confirmation = this.translate.instant(
          'ERRORS.PASSWORD.MISMATCH',
        );
        return reject(this.errors);
      }

      resolve();
    });
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.payload) {
      this.form.patchValue({
        password: this.payload.password || '',
        password_confirmation: this.payload.password_confirmation || '',
      });
    }
  }
}
